/**
 * 🌐 SmartBoutik - Indicateur de Statut Offline
 * Affiche l'état de connexion et de synchronisation à l'utilisateur
 */

import React, { useState, useEffect } from 'react';
import { Alert, Badge, Button, Spinner, ProgressBar, Collapse } from 'react-bootstrap';
import { 
  Wifi, 
  WifiOff, 
  RefreshCw, 
  Cloud, 
  CloudOff, 
  CheckCircle, 
  AlertTriangle,
  Clock,
  Database,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { useOffline } from '../contexts/OfflineContext';
import { SYNC_STATUS } from '../services/syncService';

/**
 * Barre de statut compacte (à mettre dans le header)
 */
export const OfflineStatusBar = ({ showDetails = false }) => {
  const { 
    isOnline, 
    syncStatus, 
    pendingCount, 
    lastSync, 
    forceSync,
    isSyncing 
  } = useOffline();

  const [showToast, setShowToast] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);

  // Afficher une notification quand la connexion revient
  useEffect(() => {
    if (!isOnline) {
      setWasOffline(true);
    } else if (wasOffline) {
      setShowToast(true);
      setTimeout(() => setShowToast(false), 20000);
      setWasOffline(false);
    }
  }, [isOnline, wasOffline]);

  const getBadgeVariant = () => {
    if (!isOnline) return 'danger';
    if (syncStatus === SYNC_STATUS.SYNCING) return 'warning';
    if (syncStatus === SYNC_STATUS.ERROR) return 'danger';
    if (pendingCount > 0) return 'warning';
    return 'success';
  };

  const getStatusIcon = () => {
    if (!isOnline) return <WifiOff size={14} />;
    if (isSyncing) return <RefreshCw size={14} className="spin" />;
    if (syncStatus === SYNC_STATUS.ERROR) return <AlertTriangle size={14} />;
    if (pendingCount > 0) return <Cloud size={14} />;
    return <Wifi size={14} />;
  };

  const getStatusText = () => {
    if (!isOnline) return 'Hors ligne';
    if (isSyncing) return 'Synchronisation...';
    if (syncStatus === SYNC_STATUS.ERROR) return 'Erreur sync';
    if (pendingCount > 0) return `${pendingCount} en attente`;
    return 'En ligne';
  };

  const formatLastSync = () => {
    if (!lastSync) return 'Jamais';
    const date = new Date(lastSync);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'À l\'instant';
    if (diff < 3600000) return `Il y a ${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return `Il y a ${Math.floor(diff / 3600000)}h`;
    return date.toLocaleDateString('fr-FR');
  };

  const handleSyncAndClose = () => {
    forceSync();
    setShowToast(false);
  };

  return (
    <>
      <div className="offline-status-bar d-flex align-items-center gap-2">
        <Badge 
          bg={getBadgeVariant()} 
          className="d-flex align-items-center gap-1 px-2 py-1"
          style={{ cursor: 'pointer' }}
          onClick={() => !isSyncing && isOnline && forceSync()}
          title={isOnline ? 'Cliquer pour synchroniser' : 'Mode hors ligne'}
        >
          {getStatusIcon()}
          <span className="ms-1">{getStatusText()}</span>
        </Badge>

        {isOnline && !isSyncing && pendingCount > 0 && (
          <Button 
            variant="outline-primary" 
            size="sm" 
            onClick={forceSync}
            className="py-0 px-2"
          >
            <RefreshCw size={12} className="me-1" />
            Sync
          </Button>
        )}

        {showDetails && lastSync && (
          <small className="text-muted">
            <Clock size={12} className="me-1" />
            {formatLastSync()}
          </small>
        )}
      </div>

      {/* Toast de reconnexion - 20 secondes */}
      {showToast && (
        <div 
          className="position-fixed top-0 start-50 translate-middle-x mt-3"
          style={{ zIndex: 1055, minWidth: '350px', maxWidth: '90vw' }}
        >
          <Alert 
            variant="success" 
            className="d-flex align-items-center justify-content-between py-3 mb-0 shadow-lg"
            dismissible
            onClose={() => setShowToast(false)}
          >
            <div className="d-flex align-items-center">
              <CheckCircle size={22} className="me-2 text-success" />
              <div>
                <strong>Connexion rétablie !</strong>
                {pendingCount > 0 && (
                  <div className="small mt-1">
                    {pendingCount} vente{pendingCount > 1 ? 's' : ''} en attente
                  </div>
                )}
              </div>
            </div>
            {pendingCount > 0 && (
              <Button 
                variant="success" 
                size="sm" 
                className="ms-3"
                onClick={handleSyncAndClose}
                disabled={isSyncing}
              >
                {isSyncing ? (
                  <Spinner size="sm" />
                ) : (
                  <>
                    <RefreshCw size={14} className="me-1" />
                    Synchroniser
                  </>
                )}
              </Button>
            )}
          </Alert>
        </div>
      )}

      <style>{`
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

/**
 * Bannière d'alerte offline
 */
export const OfflineBanner = () => {
  const { isOnline, pendingCount, forceSync, isSyncing } = useOffline();
  const [dismissed, setDismissed] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);
  const [showReconnected, setShowReconnected] = useState(false);

  useEffect(() => {
    if (!isOnline) {
      setWasOffline(true);
      setDismissed(false);
    } else if (wasOffline && isOnline) {
      setShowReconnected(true);
      setWasOffline(false);
      const timer = setTimeout(() => setShowReconnected(false), 20000);
      return () => clearTimeout(timer);
    }
  }, [isOnline, wasOffline]);

  useEffect(() => {
    if (!isOnline || pendingCount > 0) {
      setDismissed(false);
    }
  }, [isOnline, pendingCount]);

  if (showReconnected && pendingCount > 0) {
    return (
      <Alert 
        variant="success"
        className="mb-0 rounded-0 py-2"
        dismissible
        onClose={() => setShowReconnected(false)}
      >
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <CheckCircle size={18} className="me-2" />
            <span>
              <strong>Connexion rétablie !</strong> - {pendingCount} vente{pendingCount > 1 ? 's' : ''} en attente
            </span>
          </div>
          <Button 
            variant="success" 
            size="sm"
            onClick={() => {
              forceSync();
              setShowReconnected(false);
            }}
            disabled={isSyncing}
          >
            {isSyncing ? (
              <Spinner size="sm" className="me-1" />
            ) : (
              <RefreshCw size={14} className="me-1" />
            )}
            Synchroniser
          </Button>
        </div>
      </Alert>
    );
  }

  if (dismissed || (isOnline && pendingCount === 0)) {
    return null;
  }

  return (
    <Alert 
      variant={isOnline ? 'warning' : 'danger'}
      className="mb-0 rounded-0 py-2"
      dismissible={isOnline}
      onClose={() => setDismissed(true)}
    >
      <div className="d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center">
          {!isOnline ? (
            <>
              <WifiOff size={18} className="me-2" />
              <span>
                <strong>Mode hors ligne</strong> - Les ventes seront synchronisées à la reconnexion
                {pendingCount > 0 && (
                  <Badge bg="dark" className="ms-2">{pendingCount} en attente</Badge>
                )}
              </span>
            </>
          ) : (
            <>
              <Cloud size={18} className="me-2" />
              <span>
                <strong>{pendingCount} vente(s) en attente</strong> de synchronisation
              </span>
            </>
          )}
        </div>

        {isOnline && !isSyncing && (
          <Button variant="outline-dark" size="sm" onClick={forceSync}>
            <RefreshCw size={14} className="me-1" />
            Synchroniser
          </Button>
        )}

        {isSyncing && (
          <Spinner animation="border" size="sm" />
        )}
      </div>
    </Alert>
  );
};

/**
 * Panneau de détails de synchronisation
 */
export const SyncDetailsPanel = () => {
  const { 
    isOnline, 
    syncStatus, 
    pendingCount, 
    lastSync, 
    syncStats,
    forceSync,
    refreshStats,
    resetOfflineData,
    isSyncing,
    isInitialized
  } = useOffline();

  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isInitialized) {
      refreshStats();
    }
  }, [isInitialized, refreshStats]);

  const handleForceSync = async () => {
    setLoading(true);
    try {
      await forceSync();
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    if (window.confirm('Supprimer toutes les données locales ?')) {
      setLoading(true);
      try {
        await resetOfflineData();
      } finally {
        setLoading(false);
      }
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Jamais';
    return new Date(dateStr).toLocaleString('fr-FR');
  };

  return (
    <div className="sync-details-panel card">
      <div 
        className="card-header d-flex justify-content-between align-items-center"
        style={{ cursor: 'pointer' }}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="d-flex align-items-center">
          <Database size={18} className="me-2" />
          <strong>Synchronisation & Mode Offline</strong>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Badge bg={isOnline ? 'success' : 'danger'}>
            {isOnline ? 'En ligne' : 'Hors ligne'}
          </Badge>
          {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </div>
      </div>

      <Collapse in={expanded}>
        <div className="card-body">
          <div className="row mb-3">
            <div className="col-md-6">
              <div className="d-flex align-items-center mb-2">
                {isOnline ? (
                  <Wifi size={20} className="text-success me-2" />
                ) : (
                  <WifiOff size={20} className="text-danger me-2" />
                )}
                <span><strong>État:</strong> {isOnline ? 'Connecté' : 'Hors ligne'}</span>
              </div>
              <div className="d-flex align-items-center mb-2">
                <Clock size={20} className="text-muted me-2" />
                <span><strong>Dernière sync:</strong> {formatDate(lastSync)}</span>
              </div>
            </div>
            <div className="col-md-6">
              <div className="d-flex align-items-center mb-2">
                <Cloud size={20} className="text-warning me-2" />
                <span><strong>En attente:</strong> {pendingCount} élément(s)</span>
              </div>
              <div className="d-flex align-items-center mb-2">
                {syncStatus === SYNC_STATUS.SYNCING ? (
                  <Spinner animation="border" size="sm" className="me-2" />
                ) : syncStatus === SYNC_STATUS.ERROR ? (
                  <AlertTriangle size={20} className="text-danger me-2" />
                ) : (
                  <CheckCircle size={20} className="text-success me-2" />
                )}
                <span><strong>Statut:</strong> {syncStatus}</span>
              </div>
            </div>
          </div>

          {syncStats && (
            <div className="mb-3">
              <h6>Données en cache</h6>
              <div className="row">
                <div className="col-6 col-md-3">
                  <div className="text-center p-2 bg-light rounded">
                    <div className="fw-bold">{syncStats.PRODUCTS || 0}</div>
                    <small className="text-muted">Produits</small>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="text-center p-2 bg-light rounded">
                    <div className="fw-bold">{syncStats.CATEGORIES || 0}</div>
                    <small className="text-muted">Catégories</small>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="text-center p-2 bg-light rounded">
                    <div className="fw-bold">{syncStats.CUSTOMERS || 0}</div>
                    <small className="text-muted">Clients</small>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="text-center p-2 bg-light rounded">
                    <div className="fw-bold">{syncStats.SALES_HISTORY || 0}</div>
                    <small className="text-muted">Ventes</small>
                  </div>
                </div>
              </div>
            </div>
          )}

          {isSyncing && (
            <div className="mb-3">
              <ProgressBar animated now={100} label="Synchronisation..." />
            </div>
          )}

          <div className="d-flex gap-2 flex-wrap">
            <Button 
              variant="primary" 
              onClick={handleForceSync}
              disabled={!isOnline || loading}
            >
              {loading ? <Spinner size="sm" className="me-1" /> : <RefreshCw size={16} className="me-1" />}
              Synchroniser
            </Button>
            
            <Button variant="outline-secondary" onClick={refreshStats} disabled={loading}>
              Actualiser stats
            </Button>
            
            <Button variant="outline-danger" onClick={handleReset} disabled={loading}>
              Réinitialiser cache
            </Button>
          </div>
        </div>
      </Collapse>
    </div>
  );
};

/**
 * Indicateur de vente offline
 */
export const OfflineSaleIndicator = ({ sale }) => {
  if (!sale?.localId || sale?.status === 'synced') {
    return null;
  }

  const getVariant = () => {
    switch (sale.status) {
      case 'pending': return 'warning';
      case 'syncing': return 'info';
      case 'error': return 'danger';
      default: return 'secondary';
    }
  };

  const getText = () => {
    switch (sale.status) {
      case 'pending': return 'En attente';
      case 'syncing': return 'Sync...';
      case 'error': return 'Erreur';
      default: return 'Offline';
    }
  };

  return (
    <Badge bg={getVariant()} className="ms-2">
      {sale.status === 'syncing' ? (
        <Spinner animation="border" size="sm" className="me-1" />
      ) : (
        <CloudOff size={12} className="me-1" />
      )}
      {getText()}
    </Badge>
  );
};

export default OfflineStatusBar;